import { useEffect, useRef, useState } from "react";
import { fetchBrand, brandLogoUrl, uploadBrandLogo, updateBrandAlt, deleteBrandLogo, formatApiErrorDetail } from "@/villa142/api";
import { ConfirmModal } from "@/villa142/admin/ConfirmModal";

export default function BrandView() {
  const [brand, setBrand] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [altState, setAltState] = useState("idle");
  const fileRef = useRef(null);
  const timer = useRef(null);

  const load = async () => setBrand(await fetchBrand());
  useEffect(() => { load(); }, []);

  if (!brand) return <p className="v142-copy">Loading…</p>;

  const upload = async (file) => {
    if (!file) return;
    setError("");
    setBusy(true);
    try {
      await uploadBrandLogo(file);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveAlt = (v) => {
    setBrand({ ...brand, alt: v });
    setAltState("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        await updateBrandAlt(v);
        setAltState("saved");
      } catch {
        setAltState("error");
      }
    }, 700);
  };

  return (
    <div data-testid="v142-brand-view">
      <p className="v142-copy">
        The Ōraṇi mark appears on the entry screen and the completion screen only — Villa 142 remains the primary
        identity through the reflection itself. A transparent monochrome SVG is preferred; a high-resolution
        transparent PNG also works. When no logo is uploaded, a quiet textual ORANI lockup is shown instead.
      </p>
      {error && <p className="v142-error" role="alert" data-testid="v142-brand-error">{error}</p>}

      <div className="v142-brand-panel">
        <div className="v142-brand-preview" data-testid="v142-brand-preview">
          {brand.has_logo ? (
            <img src={brandLogoUrl(brand.updated_at)} alt={brand.alt} className="v142-brandmark" />
          ) : (
            <span className="v142-brandmark-text">ORANI</span>
          )}
          <span className="v142-entry-meta">{brand.has_logo ? "Uploaded logo" : "Text fallback (no logo uploaded)"}</span>
        </div>
        <div className="v142-brand-ctrl">
          <input
            ref={fileRef}
            type="file"
            accept=".svg,.png,image/svg+xml,image/png"
            className="v142-visually-hidden"
            id="v142-brand-file"
            data-testid="v142-brand-file"
            onChange={(e) => upload(e.target.files?.[0])}
          />
          <button type="button" className="v142-btn v142-btn-small" data-testid="v142-brand-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()}>
            {busy ? "Uploading…" : brand.has_logo ? "Replace logo" : "Upload logo"}
          </button>
          {brand.has_logo && (
            <button type="button" className="v142-link v142-danger-link" data-testid="v142-brand-remove-btn" onClick={() => setConfirm(true)}>
              Remove logo
            </button>
          )}
          <div className="v142-mini-field v142-brand-alt">
            <label htmlFor="v142-brand-alt" className="v142-mini-label">
              Alt text
              {altState === "saving" && " · Saving…"}
              {altState === "saved" && " · Saved"}
              {altState === "error" && " · Could not save"}
            </label>
            <input
              id="v142-brand-alt"
              data-testid="v142-brand-alt-input"
              className="v142-input v142-input-small"
              type="text"
              value={brand.alt || ""}
              onChange={(e) => saveAlt(e.target.value)}
            />
          </div>
        </div>
      </div>

      <ConfirmModal
        open={confirm}
        title="Remove the logo"
        body="The entry and completion screens will fall back to the textual ORANI lockup."
        note={null}
        confirmLabel="Remove"
        onCancel={() => setConfirm(false)}
        onConfirm={async () => { await deleteBrandLogo(); setConfirm(false); load(); }}
      />
    </div>
  );
}
